<script setup>
import { computed, nextTick, onMounted, ref, useId, watch } from 'vue'
import { useTypeahead } from '@composables/use-typeahead'
import { useDismissable } from '@composables/use-dismissable'
import recentKeywordsService from '@services/search/recent-keywords-service'
import { isMac } from '@utils/user-agent'
import SearchTypeahead from './search-typeahead'
import StationLauncher from './station-launcher'

const props = defineProps({
    modelValue: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: 'Search music',
    },
    // desktop = 600 cap + key hints · tablet = fills the bar · phone = fills the page block
    layout: {
        type: String,
        required: true,
        validator: value => ['desktop', 'tablet', 'phone'].includes(value),
    },
    launcher: {
        type: Boolean,
        default: false,
    },
    keys: {
        type: Boolean,
        default: false,
    },
    typeahead: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['update:modelValue', 'submit', 'clear', 'navigate'])

// useId() is stable across a server render and its hydration.
const listboxId = `search-typeahead-${useId()}`
const root = ref(null)
const input = ref(null)
const panel = ref(null)
const isFocused = ref(false)
const isKeyboardFocus = ref(false)
// D24: the panel opens only after the listener types, focuses an empty field, or presses ↓.
const isArmed = ref(false)
const activeIndex = ref(-1)
const recents = ref([])
// Resolved after mount: the platform is not knowable on a server (Story 3).
const modifierKey = ref('')
let pointerFocus = false

const hasValue = computed(() => Boolean((props.modelValue || '').trim()))
const showKeys = computed(() => props.keys && !hasValue.value)

// An unarmed field never asks the API (URL sync, programmatic focus), and neither does
// a field with no typeahead to show the answer (phone).
const typeaheadQuery = computed(() => (props.typeahead && isArmed.value ? props.modelValue : ''))
const { rows, bestMatch, isLoading, hasSearched, reset } = useTypeahead(typeaheadQuery)

const hasResults = computed(() => Boolean(bestMatch.value) || rows.value.length > 0)

// With a value: nothing until the first response (no empty panel), and stale results stay
// up while the next request is pending — but a no-match closes rather than collapse to a sliver.
const isOpen = computed(() => {
    if (!props.typeahead || !isFocused.value || !isArmed.value) return false
    if (!hasValue.value) return recents.value.length > 0
    return hasSearched.value && (hasResults.value || !isLoading.value)
})

// Mirrors the panel's no-match copy from OUTSIDE the panel: the panel unmounts while the next
// request is pending, and a live region that mounts already holding its text is not announced.
// Blanking it between requests is deliberate — it also lets the same keyword announce twice.
const announcement = computed(() =>
    isOpen.value && hasValue.value && hasSearched.value && !isLoading.value && !hasResults.value
        ? `No quick matches for ${props.modelValue.trim()}`
        : ''
)

const activeDescendant = computed(() =>
    isOpen.value && activeIndex.value >= 0 && panel.value ? panel.value.optionIds[activeIndex.value] : undefined
)

function refreshRecents() {
    recents.value = recentKeywordsService.getRecent()
}

function onInput(event) {
    isArmed.value = true
    activeIndex.value = -1
    emit('update:modelValue', event.target.value)
}

function onPointerdown() {
    pointerFocus = true
}

function onFocus() {
    isFocused.value = true
    isKeyboardFocus.value = !pointerFocus
    pointerFocus = false
    refreshRecents()
    if (!hasValue.value) isArmed.value = true
}

function onBlur() {
    isFocused.value = false
    isKeyboardFocus.value = false
    // A pointerdown on the already-focused input never reached onFocus; don't let it
    // swallow the ring on the next keyboard focus.
    pointerFocus = false
    close()
}

function close() {
    isArmed.value = false
    activeIndex.value = -1
}

// Every way of acting on a search remembers the keyword; a blank one is not a search.
// Returns the trimmed keyword, or '' when there was nothing to remember.
function remember(keyword) {
    const trimmed = (keyword || '').trim()
    if (!trimmed) return ''
    recents.value = recentKeywordsService.add(trimmed)
    return trimmed
}

function submit(keyword) {
    const trimmed = remember(keyword)
    if (!trimmed) return
    close()
    emit('submit', trimmed)
}

function activate(item) {
    if (item.kind === 'see-all') return submit(props.modelValue)
    if (item.kind === 'recent') {
        emit('update:modelValue', item.title)
        return submit(item.title)
    }
    // Opening a result is a completed search too — remember what was typed, not the row.
    remember(props.modelValue)
    close()
    emit('navigate', item.to)
}

function removeRecent(keyword) {
    recents.value = recentKeywordsService.remove(keyword)
}

// A mouse click on the disc (detail > 0) refocuses without the ring; a keyboard
// activation keeps it. The empty, focused field then shows recents (armed).
function clear(event) {
    emit('update:modelValue', '')
    reset()
    activeIndex.value = -1
    emit('clear')
    isArmed.value = true
    nextTick(() => {
        if (!input.value) return
        pointerFocus = event?.detail > 0
        input.value.focus()
    })
}

function onKeydown(event) {
    // Enter/arrows while an IME is composing belong to the IME. Safari fires the
    // composition-ending keydown with isComposing false but keyCode 229.
    if (event.isComposing || event.keyCode === 229) return
    // ⌘/Ctrl + ↑/↓ adjust the volume (key-controls-service); leave the selection alone.
    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && (event.metaKey || event.ctrlKey)) return
    const options = isOpen.value && panel.value ? panel.value.options : []
    switch (event.key) {
    case 'ArrowDown':
        event.preventDefault()
        if (!options.length) {
            if (hasValue.value) isArmed.value = true
            return
        }
        activeIndex.value = (activeIndex.value + 1) % options.length
        break
    case 'ArrowUp':
        if (!options.length) return
        event.preventDefault()
        activeIndex.value = activeIndex.value <= 0 ? options.length - 1 : activeIndex.value - 1
        break
    case 'Enter':
        event.preventDefault()
        if (activeIndex.value >= 0 && options[activeIndex.value]) activate(options[activeIndex.value])
        else submit(props.modelValue)
        break
    case 'Escape':
        event.preventDefault()
        close()
        input.value?.blur()
        break
    default:
    }
}

// ⌘S and the phone Search re-tap land here: keyboard focus, ring on.
function focus() {
    pointerFocus = false
    if (!input.value) return
    // Already focused (by a pointer): no focus event will follow, so turn the ring on here.
    if (typeof document !== 'undefined' && document.activeElement === input.value) {
        isKeyboardFocus.value = true
        return
    }
    input.value.focus()
}

useDismissable({ isOpen, onDismiss: close, inside: () => [root.value] })

// A new keyword, a new result set or an edited recents list invalidates the highlighted option.
watch([() => props.modelValue, rows, bestMatch, recents], () => {
    activeIndex.value = -1
})

onMounted(() => {
    modifierKey.value = isMac() ? '⌘' : 'Ctrl'
})

function blur() {
    input.value?.blur()
}

defineExpose({ focus, blur })
</script>

<template>
    <div
        ref="root"
        :class="['search-field', `search-field-${layout}`, { 'search-field-ring': isKeyboardFocus, 'search-field-open': isOpen }]"
        role="search"
    >
        <BaseIcon
            class="search-glyph"
            name="search"
            :size="24"
        />
        <input
            ref="input"
            class="field"
            type="text"
            role="combobox"
            aria-autocomplete="list"
            :aria-label="placeholder"
            :aria-expanded="isOpen ? 'true' : 'false'"
            :aria-controls="typeahead ? listboxId : undefined"
            :aria-activedescendant="activeDescendant"
            :placeholder="placeholder"
            :value="modelValue"
            inputmode="search"
            name="search-query"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            data-lpignore="true"
            data-form-type="other"
            @input="onInput"
            @pointerdown="onPointerdown"
            @focus="onFocus"
            @blur="onBlur"
            @keydown="onKeydown"
        >
        <span
            v-if="showKeys"
            class="keys"
            aria-hidden="true"
        >
            <kbd
                v-if="modifierKey"
                class="key-badge"
            >{{ modifierKey }}</kbd>
            <kbd class="key-badge">S</kbd>
        </span>
        <BaseButton
            v-if="hasValue"
            class="clear"
            variant="tertiary"
            size="s"
            icon-only
            icon="close"
            label="Clear search"
            type="button"
            @click="clear"
        />
        <StationLauncher v-if="launcher" />
        <!-- Outside the transition on purpose: this node must never unmount, or the message
             arrives with the region and goes unannounced. -->
        <div
            class="announcer"
            aria-live="polite"
        >{{ announcement }}</div>
        <!-- Fade + 4px settle at base/enter, fade at fast/exit (ticket "Motion"); the .settle-* rules below -->
        <transition name="settle">
            <SearchTypeahead
                v-if="typeahead && isOpen"
                ref="panel"
                :listbox-id="listboxId"
                :query="modelValue"
                :rows="rows"
                :best-match="bestMatch"
                :recents="recents"
                :has-searched="hasSearched"
                :is-loading="isLoading"
                :active-index="activeIndex"
                :hint-row="layout === 'desktop'"
                @activate="activate"
                @remove-recent="removeRecent"
            />
        </transition>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

$pill-height: 48px;
$pill-max: 600px;
$pill-padding-left: 20px;
$key-size: 24px;

.search-field {
    position: relative;
    display: flex;
    gap: $space-8;
    align-items: center;
    width: 100%;
    height: $pill-height;
    // Zero vertical padding: the launcher wrap stretches to the pill height (D27).
    padding: 0 $space-8 0 $pill-padding-left;
    background: $c-surface-overlay;
    border-radius: $radius-full;

    @include transition((background-color, box-shadow), fast);

    // Hover = state/hover wash over the overlay surface. Phones render this field;
    // iOS keeps :hover after a tap.
    @media (hover: hover) {
        &:hover {
            background: linear-gradient($c-state-hover, $c-state-hover) $c-surface-overlay;
        }
    }

    // Keyboard focus only (click focus = caret only, #350 ruling). The mixin's
    // :focus-visible cannot see the input's focus from the pill, so a class carries it.
    &.search-field-ring {
        outline: 2px solid transparent;
        box-shadow: var(--shadow-focus);
    }
}

.search-field-desktop {
    max-width: $pill-max;
}

.search-glyph {
    flex-shrink: 0;
    color: $c-text-primary;
}

.field {
    @include body;

    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0;
    color: $c-text-primary;
    background: transparent;
    border: 0;

    &::placeholder {
        color: $c-text-tertiary;
    }

    // The caret is the affordance; the pill draws the ring.
    &:focus,
    &:focus-visible {
        outline: none;
    }
}

.keys {
    display: flex;
    flex-shrink: 0;
    gap: $space-4;
    align-items: center;
    margin-right: $space-4;
}

.key-badge {
    @include captionRegular;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: $key-size;
    height: $key-size;
    padding: 0 $space-4;
    font-family: inherit;
    font-weight: 500;
    color: $c-text-tertiary;
    border: 1px solid $c-text-tertiary;
    border-radius: $radius-5;
}

// Colour comes from BaseButton's tertiary variant ($c-text-secondary).
.clear {
    flex-shrink: 0;
}

// Clip-hidden, not `display: none`/`visibility: hidden` — those are not announced. There is no
// shared visually-hidden utility in the app yet, so the recipe lives here.
.announcer {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    white-space: nowrap;
    clip-path: inset(50%);
}

// Typeahead motion: fade + 4px settle at base/enter, fade at fast/exit; reduced motion = fade.
// The panel is the transition child (its own root is position: absolute), so the parent owns the rules.
.settle-enter-active {
    @include transition((opacity, transform), base, enter);
}

.settle-leave-active {
    // The fading panel is on its way out: a click must reach what is under it.
    pointer-events: none;

    @include transition(opacity, fast, exit);
}

.settle-enter-from {
    opacity: 0;
    transform: translateY(-#{$space-4});
}

.settle-leave-to {
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .settle-enter-from {
        transform: none;
    }
}
</style>
